/**
 * The pause modal, plus the shell every modal in the cabinet shares.
 *
 * `ModalShell` lives here because `PauseMenu` is the first modal the
 * architecture contract lists, and the contract fixes the file list. GameOver
 * and Settings import it rather than each growing their own focus trap.
 */

import { el, icon, installUiTheme, money } from './Icon';
import type { IconName } from './Icon';
import type { Role } from '@/core/palette';

const FOCUSABLE =
  'button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** A scrim, a bordered box, a focus trap, and Escape. Nothing else. */
export class ModalShell {
  readonly scrim = el('div', 'scrim');
  readonly box = el('div', 'modal');
  readonly title = el('h2', 'modal__title');
  private titleIcon: HTMLElement | null = null;
  private returnFocus: HTMLElement | null = null;
  private mounted = false;

  /** Set by the owner. Escape and scrim clicks route here. */
  onDismiss: (() => void) | null = null;

  constructor(private readonly titleId: string, extraClass?: string) {
    if (extraClass) this.box.classList.add(extraClass);
  }

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();
    this.title.id = this.titleId;
    this.box.setAttribute('role', 'dialog');
    this.box.setAttribute('aria-modal', 'true');
    this.box.setAttribute('aria-labelledby', this.titleId);
    this.box.append(this.title);
    this.scrim.append(this.box);
    this.scrim.hidden = true;
    this.scrim.addEventListener('pointerdown', this.onScrim);
    this.scrim.addEventListener('keydown', this.onKey);
    root.append(this.scrim);
  }

  setTitle(text: string, name: IconName, role: Role = 'uiAccent'): void {
    if (this.titleIcon) this.titleIcon.remove();
    this.titleIcon = icon(name, { size: 24, role, glow: 1.0 });
    this.title.textContent = text;
    this.title.prepend(this.titleIcon);
  }

  get open(): boolean {
    return !this.scrim.hidden;
  }

  setOpen(open: boolean): void {
    if (open === this.open) return;
    if (open) {
      const active = document.activeElement;
      this.returnFocus = active instanceof HTMLElement ? active : null;
      this.scrim.hidden = false;
      this.focusFirst();
    } else {
      this.scrim.hidden = true;
      this.returnFocus?.focus();
      this.returnFocus = null;
    }
  }

  focusFirst(): void {
    this.box.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  }

  destroy(): void {
    this.scrim.removeEventListener('pointerdown', this.onScrim);
    this.scrim.removeEventListener('keydown', this.onKey);
    this.scrim.remove();
    this.mounted = false;
  }

  private readonly onScrim = (ev: PointerEvent): void => {
    if (ev.target === this.scrim) this.onDismiss?.();
  };

  private readonly onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      this.onDismiss?.();
      return;
    }
    if (ev.key !== 'Tab') return;
    const items = [...this.box.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (ev.shiftKey && active === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && active === last) {
      ev.preventDefault();
      first.focus();
    }
  };
}

// ────────────────────────────────────────────────────────────── pause menu ──

export interface PauseMenuState {
  open: boolean;
  round: number;
  cash: number;
  lives: number;
}

export interface PauseMenuCallbacks {
  onResume?(): void;
  onRestart?(): void;
  onSettings?(): void;
  onQuit?(): void;
}

export class PauseMenu {
  private shell = new ModalShell('pause-title');
  private roundOut = el('dd', 'num', '0');
  private cashOut = el('dd', 'num', '$0');
  private livesOut = el('dd', 'num', '0');
  private mounted = false;

  constructor(private readonly cb: PauseMenuCallbacks = {}) {}

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    this.shell.mount(root);
    this.shell.setTitle('Paused', 'pause');
    this.shell.onDismiss = () => this.cb.onResume?.();

    const score = el('dl', 'score');
    score.append(
      this.scoreLabel('flag-checkered', 'Round'), this.roundOut,
      this.scoreLabel('coins', 'Cash'), this.cashOut,
      this.scoreLabel('heart', 'Lives'), this.livesOut,
    );

    const actions = el('div', 'modal__actions');
    actions.append(
      this.action('play', 'Resume', 'btn btn--primary', () => this.cb.onResume?.()),
      this.action('cog', 'Settings', 'btn', () => this.cb.onSettings?.()),
      this.action('refresh', 'Restart run', 'btn', () => this.cb.onRestart?.()),
      this.action('logout', 'Quit to menu', 'btn btn--danger', () => this.cb.onQuit?.()),
    );

    this.shell.box.append(score, actions);
  }

  update(state: PauseMenuState): void {
    if (!this.mounted) return;
    this.roundOut.textContent = String(state.round);
    this.cashOut.textContent = money(state.cash);
    this.livesOut.textContent = String(state.lives);
    this.shell.setOpen(state.open);
  }

  destroy(): void {
    this.shell.destroy();
    this.mounted = false;
  }

  private scoreLabel(name: IconName, text: string): HTMLElement {
    const dt = el('dt');
    dt.append(icon(name, { size: 12 }), document.createTextNode(text));
    return dt;
  }

  private action(name: IconName, text: string, cls: string, fn: () => void): HTMLElement {
    const btn = el('button', cls);
    btn.type = 'button';
    btn.append(icon(name, { size: 16 }), document.createTextNode(text));
    btn.addEventListener('click', fn);
    return btn;
  }
}
